import { isDefined } from "@clipboard-health/util-ts";
import { CbhFeatureFlag } from "@src/appV2/FeatureFlags/CbhFeatureFlag";
import { useCbhFlag } from "@src/appV2/FeatureFlags/useCbhFlag";
import { APP_V2_USER_EVENTS, logEvent } from "@src/appV2/lib/analytics";
import { useLogEffect } from "@src/appV2/lib/analytics/useLogEffect";
import { useToast } from "@src/appV2/lib/Notifications/Toasts/useToast";
import { useLocalStorage } from "@src/appV2/lib/utils/useLocalstorage";
import { useModalState } from "@src/appV2/redesign/system/ui-components";
import { useDefinedWorker } from "@src/appV2/Worker/useDefinedWorker";
import { useCallback, useState } from "react";
import Confetti from "react-confetti";

import {
  getTravelShiftsStorageKey,
  TRAVEL_SHIFTS_RESPONSES,
  type TravelShiftsResponse,
} from "./constants";
import { TravelShiftsBanner } from "./TravelShiftsBanner";
import { TravelShiftsBottomSheet } from "./TravelShiftsBottomSheet";

export function TravelShiftsEntrypoint() {
  const bottomSheetModalState = useModalState();
  const { userId } = useDefinedWorker();
  const { showInfoToast } = useToast();
  const [showConfetti, setShowConfetti] = useState(false);

  const isEnabled = useCbhFlag(CbhFeatureFlag.TRAVEL_SHIFTS, {
    defaultValue: false,
  });

  const storageKey = getTravelShiftsStorageKey(userId);

  const [travelShiftsResponse, setTravelShiftsResponse] = useLocalStorage<
    TravelShiftsResponse | undefined
  >(storageKey, undefined);

  const shouldShowBanner = isEnabled && !isDefined(travelShiftsResponse);

  useLogEffect(
    APP_V2_USER_EVENTS.TRAVEL_SHIFTS_BANNER_VIEWED,
    { workerId: userId },
    { enabled: shouldShowBanner }
  );

  const handleBannerClick = useCallback(() => {
    logEvent(APP_V2_USER_EVENTS.TRAVEL_SHIFTS_BANNER_CTA_CLICKED, {
      workerId: userId,
    });
    bottomSheetModalState.openModal();
  }, [userId, bottomSheetModalState]);

  const handleDismiss = useCallback(() => {
    setTravelShiftsResponse(TRAVEL_SHIFTS_RESPONSES.NOT_INTERESTED);
  }, [setTravelShiftsResponse]);

  const handleSubmit = useCallback(
    (response: TravelShiftsResponse) => {
      setTravelShiftsResponse(response);

      showInfoToast("Thanks for letting us know!");

      if (response === TRAVEL_SHIFTS_RESPONSES.INTERESTED) {
        setShowConfetti(true);
      }
    },
    [setTravelShiftsResponse, showInfoToast]
  );

  if (!shouldShowBanner && !showConfetti) {
    return null;
  }

  return (
    <>
      {showConfetti && (
        <Confetti
          width={window.innerWidth}
          height={window.innerHeight}
          numberOfPieces={400}
          gravity={0.8}
          recycle={false}
          onConfettiComplete={() => {
            setShowConfetti(false);
          }}
        />
      )}
      {shouldShowBanner && (
        <>
          <TravelShiftsBanner onClick={handleBannerClick} onDismiss={handleDismiss} />
          <TravelShiftsBottomSheet
            modalState={bottomSheetModalState}
            workerId={userId}
            onSubmit={handleSubmit}
          />
        </>
      )}
    </>
  );
}
